/**
 * @file Coquille widget du flow de réservation de table.
 * @module widgets.TableReservation.view.TableReservationView
 *
 * Concentre tout le spécifique widget (authentification par clé API, resize
 * iframe, theming, multilingue, branding, états de chargement/erreur) puis
 * monte le flow partagé `ReservationFlow` de `packages/common`. La logique de
 * parcours, de paiement et de formules vit entièrement dans le flow ; cette
 * coquille ne fait que résoudre la configuration et l'i18n, construire les
 * slots de chrome (sélecteur de langue, branding) et injecter la source de
 * données via `ReservationDataProvider`.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {useSearchParams} from 'react-router-dom';

import {useWidgetBase} from '../../common/hooks/useWidgetBase';
import {WidgetStateHandler} from '../../common/components/WidgetStateHandler';
import WidgetErrorBoundary from '../../common/components/WidgetErrorBoundary';
import {IframeResizeProvider} from '../../common/contexts/IframeResizeContext';
import {BrandingFooter} from 'packages/common/components/branding-footer';
import {FlowLanguageSelector} from 'packages/common/components/flow-language-selector';
import {useWidgetLanguageSetup} from '../../common/hooks/useWidgetLanguageSetup';

import {useExternalReservationConfig} from 'packages/common/hooks/api/widgets/restaurants/Reservations';
import {ReservationDataProvider} from 'packages/common/components/table-reservation/context/ReservationDataContext';
import {ReservationFlow} from 'packages/common/components/table-reservation/ReservationFlow';
import {useWidgetReservationDataAdapter} from '../adapters/useWidgetReservationDataAdapter';

import {isInIframe} from '../../../utils/iframeUtils';
import {getWidgetDimensions, WIDGET_TYPES} from '../../config';

/**
 * Point d'entrée du widget de réservation.
 *
 * Fournit le contexte IframeResize, l'error boundary et l'adapter de données à
 * clé API, puis délègue à `TableReservationContent` pour le rendu réel.
 *
 * @param {Object} props
 * @param {string} [props.mode='fixed'] - Mode d'affichage ('fixed' ou 'floating')
 * @param {string} [props.companyId] - ID de l'entreprise (priorité sur les paramètres URL)
 * @param {string} [props.apiKey] - Clé API publique (priorité sur les paramètres URL)
 * @return {JSX.Element} Widget de réservation
 */
export default function TableReservationView({
  mode = 'fixed',
  buttonText = 'Réserver une table',
  companyId: propCompanyId = null,
  apiKey: propApiKey = null,
}) {
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get('mode');

  const embedded = isInIframe();
  const displayMode = (urlMode || mode).toLowerCase().trim();
  const companyId = propCompanyId || searchParams.get('companyId') || null;
  const apiKey = propApiKey || searchParams.get('apiKey') || null;
  const instanceId = searchParams.get('instanceId') || 'default-instance';
  const customColor = searchParams.get('customColor');
  const dimensions = getWidgetDimensions(WIDGET_TYPES.RESERVATIONS, displayMode);

  // Adapter de données câblé sur l'API externe à clé. Injecté au flow partagé
  // via ReservationDataProvider : le site public fournira l'équivalent par slug.
  const dataAdapter = useWidgetReservationDataAdapter(companyId, apiKey);

  return (
    <IframeResizeProvider
      mode={displayMode}
      instanceId={instanceId}
      minHeight={dimensions.minHeight}
      maxHeight={dimensions.maxHeight}
      debounceDelay={dimensions.debounceDelay}
    >
      <WidgetErrorBoundary widgetType={WIDGET_TYPES.RESERVATIONS}>
        <ReservationDataProvider value={dataAdapter}>
          <TableReservationContent
            displayMode={displayMode}
            embedded={embedded}
            companyId={companyId}
            apiKey={apiKey}
            instanceId={instanceId}
            customColor={customColor}
          />
        </ReservationDataProvider>
      </WidgetErrorBoundary>
    </IframeResizeProvider>
  );
}

/**
 * Résout la configuration, l'i18n, le branding et les états de chargement
 * spécifiques au widget, puis monte le flow partagé sous le gestionnaire d'état.
 *
 * @param {Object} props
 * @param {'fixed'|'floating'} props.displayMode - Mode d'affichage du widget
 * @param {boolean} props.embedded - Widget intégré dans une page hôte
 * @param {string} [props.companyId] - Identifiant de l'établissement
 * @param {string} [props.apiKey] - Clé API du widget
 * @param {string} props.instanceId - Identifiant unique de l'instance
 * @param {string} [props.customColor] - Couleur primaire personnalisée
 * @return {JSX.Element}
 */
function TableReservationContent({
  displayMode,
  embedded,
  companyId,
  apiKey,
  instanceId,
  customColor,
}) {
  const [searchParams] = useSearchParams();

  const {
    containerRef,
    updateHeight,
    companyDetails,
    isCompanyLoading,
    companyError,
    handleCloseWidget,
    primaryColor,
  } = useWidgetBase({
    companyId,
    apiKey,
    embedded,
    displayMode,
    instanceId,
    customColor,
  });

  const {
    config,
    isLoading: loadingConfig,
    isError: configError,
    error: configApiError,
    mutate: reloadConfig,
  } = useExternalReservationConfig(companyId, apiKey);

  // widgetConfig passe pour que getCustomText accède aux contenus personnalisables de RestaurantConfig
  const {
    getGenericText,
    getCustomText,
    currentLanguage,
    isMultiLanguageEnabled,
    enabledLanguages,
    isLoading: isTranslationsLoading,
    handleLanguageChange,
  } = useWidgetLanguageSetup({
    apiKey,
    widgetType: 'reservation',
    companyDetails,
    embedded,
    instanceId,
    searchParams,
    widgetConfig: config,
  });

  const currentError = (() => {
    if (companyError) return companyError;
    if (configError) return configApiError;
    return null;
  })();

  // Sélecteur de langue injecté dans l'en-tête du flow (chrome widget).
  const headerSlot = isMultiLanguageEnabled && enabledLanguages ? (
    <FlowLanguageSelector
      currentLanguage={currentLanguage}
      availableLanguages={enabledLanguages}
      onLanguageChange={handleLanguageChange}
      primaryColor={primaryColor}
      ariaLabel={getGenericText('widgets.languageSelector.ariaLabel')}
    />
  ) : null;

  return (
    <WidgetStateHandler
      isLoading={loadingConfig || isCompanyLoading || isTranslationsLoading}
      error={currentError}
      embedded={embedded}
      displayMode={displayMode}
      containerRef={containerRef}
      onClose={handleCloseWidget}
      widgetType={WIDGET_TYPES.RESERVATIONS}
      loadingMessage={getGenericText('common.loading')}
    >
      <ReservationFlow
        config={config}
        companyDetails={companyDetails}
        primaryColor={primaryColor}
        companyId={companyId}
        t={getGenericText}
        getCustomText={getCustomText}
        currentLanguage={currentLanguage}
        reloadConfig={reloadConfig}
        headerSlot={headerSlot}
        footerSlot={<BrandingFooter widgetType={WIDGET_TYPES.RESERVATIONS} t={getGenericText} />}
        onClose={handleCloseWidget}
        onContentResize={updateHeight}
        containerRef={containerRef}
        embedded={embedded}
        displayMode={displayMode}
        title={getGenericText('widgets.reservation.title')}
        confirmationTitle={getGenericText('widgets.reservation.messages.success')}
      />
    </WidgetStateHandler>
  );
}

TableReservationContent.propTypes = {
  displayMode: PropTypes.oneOf(['fixed', 'floating']).isRequired,
  embedded: PropTypes.bool.isRequired,
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
  instanceId: PropTypes.string.isRequired,
  customColor: PropTypes.string,
};

TableReservationView.propTypes = {
  mode: PropTypes.oneOf(['fixed', 'floating']),
  buttonText: PropTypes.string,
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
};
